import { months } from '@core/referential/date.constant'
import { Frequency } from '@core/referential/frequency.enum'
import dayjs, { Dayjs } from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import { t } from 'i18next'
import { type DateRange } from 'react-day-picker'

dayjs.extend(advancedFormat)

export type DateRangeString = {
  startDate: string
  endDate: string
}

export const dateFormat = 'YYYY-MM-DD'

export function parseDateRange({ startDate, endDate }: { startDate: Date; endDate: Date }): { startDate: string; endDate: string } {
  return {
    startDate: dayjs(startDate).format(dateFormat),
    endDate: dayjs(endDate).format(dateFormat),
  }
}

export function parseDateRangeString({ startDate, endDate }: DateRangeString): { from: Date; to: Date } {
  return {
    from: dayjs(startDate).toDate(),
    to: dayjs(endDate).toDate(),
  }
}

export function parseDurationMinutes(durationMinutes: number): { value: number; unit: 'minutes' | 'hours' | 'days' } {
  const duration = dayjs.duration(durationMinutes, 'minutes')
  const days = duration.days()
  const hours = duration.hours()
  const minutes = duration.minutes()

  if (minutes === 0) {
    if (hours === 0) {
      if (days === 0) {
        return { value: 0, unit: 'minutes' }
      }

      return { value: days, unit: 'days' }
    }

    return { value: hours, unit: 'hours' }
  }

  return { value: durationMinutes, unit: 'minutes' }
}

export function getExtremes(startTime: Date, endTime: Date | null) {
  return [dayjs(startTime).subtract(1, 'h').valueOf(), endTime ? dayjs(endTime).valueOf() : dayjs().valueOf()]
}

export function getWeeklyDateRange({ from, to }: DateRange): { from: Date; to: Date } {
  const startDate = dayjs(from)
  const endDate = dayjs(to)

  let monday
  let sunday

  if (startDate.day() === 0) {
    monday = startDate.day(1).subtract(1, 'week')
  } else {
    monday = startDate.day(1)
  }

  if (from === to && startDate.day() === 0) {
    sunday = startDate
  } else if (endDate.day() === 0) {
    sunday = endDate
  } else {
    sunday = endDate.day(0).add(1, 'week')
  }

  return { from: monday.toDate(), to: sunday.toDate() }
}

export function getMonthlyDateRange({ from, to }: DateRange): { from: Date; to: Date } {
  const startDate = dayjs(from)
  const endDate = dayjs(to)

  const startOfMonth = startDate.startOf('month')
  const endOfMonth = endDate.endOf('month')

  return { from: startOfMonth.toDate(), to: endOfMonth.toDate() }
}

export function transformDateRange({ from, to }: { from: Date; to: Date }, frequency?: Frequency): { from: Date; to: Date } {
  switch (frequency) {
    case Frequency.WEEKLY: {
      return getWeeklyDateRange({ from, to })
    }

    case Frequency.MONTHLY: {
      return getMonthlyDateRange({ from, to })
    }

    case Frequency.YEARLY: {
      return { from: dayjs(from).startOf('year').toDate(), to: dayjs(to).endOf('year').toDate() }
    }

    default: {
      return { from, to }
    }
  }
}

export function getDateRange(frequency: Frequency): { from: Date; to: Date } {
  switch (frequency) {
    case Frequency.WEEKLY: {
      return { from: dayjs().startOf('week').subtract(3, 'week').toDate(), to: dayjs().endOf('week').toDate() }
    }

    case Frequency.MONTHLY: {
      return getMonthlyDateRange({ from: dayjs().startOf('month').subtract(1, 'month').toDate(), to: dayjs().endOf('month').toDate() })
    }

    case Frequency.YEARLY: {
      return { from: dayjs().startOf('year').subtract(1, 'year').toDate(), to: dayjs().endOf('year').toDate() }
    }

    default: {
      return { from: dayjs().subtract(7, 'day').toDate(), to: dayjs().toDate() }
    }
  }
}

export function getFormattedMonth(month: string, mode: 'long' | 'short'): string {
  const shortMonths = t('months', { returnObjects: true })
  const longMonths = months

  if (mode === 'long') {
    return longMonths[shortMonths.indexOf(month)]
  }

  return shortMonths[longMonths.indexOf(month)]
}

export const timeRangeOptions = ['1h', '3h', '6h', '24h', '7d', 'custom'] as const

export type TimeRange = (typeof timeRangeOptions)[number]

export function getMode(timeRange: TimeRange): 'hour' | undefined {
  switch (timeRange) {
    case '1h':
    case '3h':
    case '6h':
    case '24h': {
      return 'hour'
    }

    case '7d':
    case 'custom': {
      return undefined
    }
  }
}

/**
 * Date range a time-range preset stands for, exactly as `TimeRangeSelect` computes it when the user picks it: ISO
 * instants for the hour presets (the API reads them as UTC datetimes), calendar days for the rest. Components seed
 * their default state with it so the initial query matches what the select would ask for.
 */
export function getTimeRangeDateRange(timeRange: TimeRange): DateRangeString {
  const now = dayjs()
  switch (timeRange) {
    case '1h':
    case '3h':
    case '6h':
    case '24h': {
      // The preset literal carries its own duration ('24h' → 24).
      const hours = Number.parseInt(timeRange, 10)

      return { startDate: now.subtract(hours, 'hour').toISOString(), endDate: now.toISOString() }
    }

    case '7d':
    case 'custom': {
      return { startDate: now.subtract(7, 'day').format(dateFormat), endDate: now.format(dateFormat) }
    }
  }
}

export function getDateRangeCompare({
  rangeType,
  year,
  compareYear,
  format = dateFormat,
  addOneDay = false,
  startDate,
  endDate,
  compareStartDate,
  compareEndDate,
}: {
  rangeType: 'last12Months' | 'year' | 'custom'
  startDate?: string
  endDate?: string
  compareStartDate?: string
  compareEndDate?: string
  year?: number
  compareYear?: number
  format?: string
  addOneDay?: boolean
}) {
  let start: Dayjs
  let end: Dayjs
  let comparedStart: Dayjs | undefined
  let comparedEnd: Dayjs | undefined
  const today = dayjs()

  if (rangeType === 'last12Months') {
    end = today.startOf('month').subtract(1, 'day')
    start = today.startOf('month').subtract(1, 'year')
    comparedEnd = end.subtract(1, 'year')
    comparedStart = start.subtract(1, 'year')
  } else if (rangeType === 'year' && year) {
    const selectedYear = year
    start = today.year(selectedYear).startOf('year')

    if (selectedYear > today.year()) {
      throw new Error('Selected year cannot be in the future')
    }

    if (selectedYear === today.year()) {
      end = today.startOf('month').subtract(1, 'day')
    } else if (compareYear === today.year()) {
      end = today.year(selectedYear).startOf('month').subtract(1, 'day')
    } else {
      end = start.add(1, 'year').subtract(1, 'day')
    }

    comparedStart = compareYear ? start.year(compareYear) : undefined
    comparedEnd = compareYear ? end.year(compareYear).endOf('month') : undefined
  } else if (rangeType === 'custom' && startDate && endDate) {
    start = dayjs(startDate)
    end = dayjs(endDate)
    comparedStart = dayjs(compareStartDate)
    comparedEnd = dayjs(compareEndDate)
  } else {
    throw new Error('Invalid selection or year')
  }

  if (addOneDay) {
    end = end.add(1, 'day')
    comparedEnd = comparedEnd ? comparedEnd.add(1, 'day') : undefined
  }

  return {
    startDate: start.format(format),
    endDate: end.format(format),
    compareStartDate: comparedStart ? comparedStart.format(format) : undefined,
    compareEndDate: comparedEnd ? comparedEnd.format(format) : undefined,
  }
}
